
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { ItemsActionName, ItemsActionNames } from '@/definitions/app/item.actions.name';
import { ItemViewModel } from '@/definitions/view-models';
import { WatchList } from '@/api';
import { ItemAclResult, ModelAclResult } from '@/store/acl/types';
import { actionHandler } from '@/store/data/ActionHandler';
import { actionsModule } from '@/store/data/ActionsModule';
import { MultisidebarItem } from '@/store/multisidebar/types';
import { Action } from '@/uikit';
import NForm from '@/uikit/forms/NForm.vue';
import NTabs from '@/uikit/tabs/NTabs.vue';
import MultisidebarHeader from '@/components/multisidebar/MultisidebarHeader.vue';
import MultisidebarSaveActions from '@/components/multisidebar/MultisidebarSaveActions.vue';
import { loadAndSyncPermissions, PermissionsModelNamesMap } from '@/components/permissions/permission-helpers';
import { getInformationLayout, getPermissionsLayout } from '@/pages/watch-lists/watch-list-form';
import CardMonitoringStatus from '@/components/monitoring/CardMonitoringStatus.vue';
import { CardTypesMap } from '@/store/application/data.assets';
import { monitoringModule } from '@/components/monitoring/MonitoringModule';
import {flatten} from "lodash";
import {RemoteMonitoringRecordRequest} from "@/api/models/RemoteMonitoringRecordRequest";
import {RemoteMonitoringRecord} from "@/api/models/RemoteMonitoringRecord";
import {configModule} from "@/store/config";
import {dataModule} from "@/store/data";

const Tabs = {
  Information: 'information',
  Permissions: 'permissions'
};

@Options({
  name: 'WatchListsSidebar',
  components: { CardMonitoringStatus, MultisidebarHeader, MultisidebarSaveActions, NForm, NTabs }
})
export default class WatchListsSidebar extends Vue {
  @Prop({ type: Object, required: true })
  readonly sidebarItem!: MultisidebarItem<ItemViewModel<WatchList>>;

  @Prop({ type: Object, required: true })
  readonly modelAcl!: ModelAclResult;

  @Prop({ type: Object, required: true })
  readonly itemAcl!: ItemAclResult;

  activeTab = Tabs.Information;

  get isPuppeteerEnabled() {
    return monitoringModule.isPuppeteerEnabled;
  }

  get currentMonitoringRecord() {
    const id = this.model.item!.id;
    return monitoringModule.watchListInRemoteMonitoring[id] ? monitoringModule.remoteMonitoringItemsByWatchList[id]?.[0] : null;
  }

  get isFormDisabled() {
    return !(this.sidebarItem.model.isNew ? this.modelAcl.add : this.itemAcl.update);
  }

  get model() {
    return this.sidebarItem.model;
  }

  get tabItems() {
    const info = {
      name: Tabs.Information,
      label: this.$t(`common.${Tabs.Information}`, 'f')
    };
    const permissions = {
      name: Tabs.Permissions,
      label: this.$t(`common.${Tabs.Permissions}`, 'f')
    };

    return this.model.isNew ? [info] : [info, permissions];
  }

  get itemForm() {
    let activeTab = this.activeTab;
    let currentTypedItem = this.model?.item;
    if (currentTypedItem) {
      return activeTab === Tabs.Permissions ? getPermissionsLayout() : getInformationLayout(this.model.isNew);
    }
    return [];
  }

  get headerActions(): Action[] {
    const isMonitoringActionsAvailable = configModule.features.watch_lists_monitoring_enabled && dataModule.isCurrentUserAdmin;

    return actionsModule
      .getItemActions(this.itemAcl || this.modelAcl, {
        hasChanges: this.model?.hasChanges,
        currentActive: this.model.item?.active,
        hasActive: true,
        hasDelete: true,
        hasAddToMonitoring: isMonitoringActionsAvailable && !this.currentMonitoringRecord,
        hasRemoveFromMonitoring: isMonitoringActionsAvailable && !!this.currentMonitoringRecord,
        hasDeleteCard: true
      })
      .map(actionsModule.computeActionByName);
  }

  get disableActions() {
    const id = this.model.item?.id || -1;
    return id < 0;
  }


  @Watch('model.item.id')
  @Watch('activeTab', { immediate: true })
  async loadPermissionsOnPermissionTab() {
    if (this.activeTab === Tabs.Permissions) {
      await loadAndSyncPermissions(this.sidebarItem.model, PermissionsModelNamesMap.WatchLists);
    }
  }

  @Watch('model.item.id')
  @Watch('hasAnyMonitoringRecord')
  async loadMonitoringRecords() {
    if (!this.isPuppeteerEnabled || this.model.item!.id < 0) return;
    await monitoringModule.getItemsByWatchList(this.model.item!.id);
  }

  validateForm(): boolean {
    const result = this.$refs.form ? this.$refs.form.validate() : true;
    if (!result) {
      this.$refs.form.displayErrors();
    }
    return result;
  }

  handleAction(actionName: ItemsActionName) {
    actionHandler.run(actionName, this.sidebarItem);
  }

  created() {
    actionHandler.getIsItemFormValid = this.validateForm.bind(this);
    this.loadMonitoringRecords();
  }


}
